import { z } from 'zod';

import { UnreachableCaseError } from './errors';
import { type MinimumTupleArray, type NonEmptyArray } from './types';

type DefinedNonNullAssertion = <T>(
  value: null | T | undefined,
  errorMessage?: string,
) => asserts value is T;

export const assertIsDefinedAndNonNull: DefinedNonNullAssertion = <T>(
  value: null | T | undefined,
  errorMessage?: string,
) => {
  if (value === undefined || value === null) {
    const defaultMessage =
      value === undefined
        ? 'Encountered an undefined value when it was not expected!'
        : 'Encountered a null value when it was not expected!';
    throw new Error(errorMessage ?? defaultMessage);
  }
};

export const ensureIsDefinedAndNonNull = <T>(value: null | T | undefined): T => {
  assertIsDefinedAndNonNull(value);
  return value;
};

type NonEmptyArrayAssertion = <T>(
  value: T[],
  errorMessage?: string,
) => asserts value is NonEmptyArray<T>;

export const assertArrayIsNotEmpty: NonEmptyArrayAssertion = <T>(
  value: T[],
  errorMessage?: string,
) => {
  if (!isNonEmptyArray(value)) {
    throw new Error(
      errorMessage ??
        'Encountered an empty array when it was expected that the array have a non-zero length!',
    );
  }
};

export const ensureArrayIsNonEmpty = <T>(value: T[], errorMessage?: string): NonEmptyArray<T> => {
  assertArrayIsNotEmpty(value, errorMessage);
  return value;
};

export const isNonEmptyArray = <T>(value: T[]): value is NonEmptyArray<T> => value.length > 0;

type MinimumTupleArrayAssertion = <T>(
  value: T[],
  errorMessage?: string,
) => asserts value is MinimumTupleArray<T>;

export const assertArrayIsMinimumTuple: MinimumTupleArrayAssertion = <T>(
  value: T[],
  errorMessage?: string,
) => {
  if (value.length <= 1) {
    throw new Error(
      errorMessage ??
        'Encountered an array with less than 2 elements when it was expected that the array ' +
          'have a length of at least 2!',
    );
  }
};

export const ensureArrayIsMinimumTuple = <T>(
  value: T[],
  errorMessage?: string,
): MinimumTupleArray<T> => {
  assertArrayIsMinimumTuple(value, errorMessage);
  return value;
};

export const UuidSchema = z.string().uuid();

export const isUuid = (value: unknown): value is string => UuidSchema.safeParse(value).success;

/**
 * A function that can be used to ensure TypeScript exhaustive type checking in conditional flows
 * by ensuring that the provided value, which should represent an unreachable code path due to
 * exhaustive type checking, is in fact an unreachable code path.
 *
 * This method can be used to ensure that a TypeScript compilation error occurs before the code
 * introduces a runtime bug after compilation.  This helps establish a **higher degree of type
 * safety** in conditional logic or flows that deal with union types or discriminated unions by
 * ensuring that all possible cases are handled explicitly and fallthrough cases do not exist.
 *
 * The method will still throw an {@link UnreachableCaseError} at runtime if it is ever invoked
 * (which would only happen if the unreachable code path became reachable) but this execution should
 * never happen if the code is properly type-checked by TypeScript and compiled before hand.
 *
 * @param {never} _value
 *   The value that represents the unreachable case that should result in a compilation error if
 *   the code path ever becomes reachable.
 *
 * @see https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-never-type for more
 * information on the 'never' type in TypeScript, type narrowing and exhaustive type checking.
 *
 * @example
 * An example of type UNSAFE code where the logic for defining the email falls back to the
 * assumption that the user is a learner if they are not an admin or instructor.  This
 * assumption could lead to runtime issues if a new role is added in the future and this code
 * is not updated to handle the new role explicitly.
 *
 * ```ts
 * const role: 'admin' | 'instructor' | 'learner';
 * await prisma.user.create({
 *   data: {
 *     email: role === 'admin'
 *       ? 'test+admin@craft.edu'
 *       : role === 'instructor'
 *         ? 'test+instructor@craft.edu' : 'test+learner@craft.edu',
 *   }
 * })
 * ```
 *
 * An example of type SAFE code where the logic for defining the email explicitly handles all
 * possible roles and uses the 'assertNever' method to ensure that if the definition of 'role'
 * is ever altered, this code will fail to compile until it is updated to handle the new role.
 *
 * ```ts
 * const role: 'admin' | 'instructor' | 'learner';
 * await prisma.user.create({
 *   data: {
 *     email: role === 'admin'
 *       ? 'test+admin@craft.edu'
 *       : role === 'instructor'
 *         ? 'test+instructor@craft.edu'
 *         : role === 'learner'
 *             ? 'test+learner@craft.edu'
 *             : assertNever(role), // Ensures exhaustive type checking
 *   }
 * })
 * ```
 *
 * @throws {UnreachableCaseError}
 *   If it is ever invoked in a runtime environment, which can only happen if the code was not
 *   properly type-checked or compiled.
 */
export const assertNever = (_value: never): never => {
  throw new UnreachableCaseError(
    'Expected the value to represent an unreachable case when it did not!',
  );
};

/**
 * A function that can be used to ensure TypeScript exhaustive type checking in conditional flows
 * by ensuring that the provided value, which should represent an unreachable code path due to
 * exhaustive type checking, is undefined.
 *
 * @param {undefined} _value
 *   The value that represents the unreachable case that should result in a compilation error if
 *   the code path represented by the value not being undefined ever becomes reachable.
 *
 * @see {@link assertNever} For a similar concept.
 * @see https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-never-type
 *
 * @throws {UnreachableCaseError}
 *   If it is ever invoked in a runtime environment, which can only happen if the code was not
 *   properly type-checked or compiled.
 */
export const assertUndefined = (value: undefined): asserts value is undefined => {
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
  if (value !== undefined) {
    throw new UnreachableCaseError('Expected the value to be undefined when it was not!');
  }
};

type NonNullableAssertion = <T>(value: NonNullable<T>) => asserts value is NonNullable<T>;

/**
 * A function that can be used to ensure TypeScript exhaustive type checking in conditional flows
 * by ensuring that the provided value, which should represent an unreachable code path due to
 * exhaustive type checking, is non-null and defined.
 *
 * @param {null} _value
 *   The value that represents the unreachable case that should result in a compilation error if
 *   the code path represented by the value being null or undefined ever becomes reachable.
 *
 * @see {@link assertNever} For a similar concept.
 * @see https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-never-type
 *
 * @throws {UnreachableCaseError}
 *   If it is ever invoked in a runtime environment, which can only happen if the code was not
 *   properly type-checked or compiled.
 */
export const assertNonNullable: NonNullableAssertion = <T>(
  value: NonNullable<T>,
): asserts value is NonNullable<T> => {
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
  if (value === null || value === undefined) {
    throw new UnreachableCaseError(
      'Expected the value to be non-null and defined when it was not!',
    );
  }
};

/**
 * A function that can be used to ensure TypeScript exhaustive type checking in conditional flows
 * by ensuring that the provided value, which should represent an unreachable code path due to
 * exhaustive type checking, is null.
 *
 * @param {null} _value
 *   The value that represents the unreachable case that should result in a compilation error if
 *   the code path represented by the value not being null ever becomes reachable.
 *
 * @see {@link assertNever} For a similar concept.
 * @see https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-never-type
 *
 * @throws {UnreachableCaseError}
 *   If it is ever invoked in a runtime environment, which can only happen if the code was not
 *   properly type-checked or compiled.
 */
export const assertNull = (value: null): asserts value is null => {
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
  if (value !== null) {
    throw new UnreachableCaseError('Expected the value to be undefined when it was not!');
  }
};
